import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Pencil,
  Trash2,
  Loader2,
  ChevronRight,
  ChevronDown,
  Network,
  MapPin,
  BadgeCheck,
  RefreshCw,
  CloudDownload,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import AdminLayout, { AdminPageHeader } from "./layout";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

interface NhsOrg {
  id: number;
  odsCode: string;
  name: string;
  shortName: string | null;
  orgType: string | null;
  region: string | null;
  status: string;
  adminAdded: boolean;
  createdAt: string;
}

interface NhsSite {
  id: number;
  odsCode: string;
  orgOdsCode: string;
  name: string;
  address: string | null;
  city: string | null;
  postcode: string | null;
  status: string;
  adminAdded: boolean;
}

async function apiFetch(path: string, opts?: RequestInit) {
  const res = await fetch(`${BASE}${path}`, { credentials: "include", ...opts });
  if (!res.ok) {
    const b = await res.json().catch(() => ({}));
    throw new Error((b as { error?: string }).error ?? "Request failed");
  }
  return res.json();
}

const EMPTY_ORG = { odsCode: "", name: "", shortName: "", orgType: "", region: "" };
const EMPTY_SITE = { odsCode: "", name: "", address: "", city: "", postcode: "" };

export default function AdminNhsOrganisations() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [expandedOrg, setExpandedOrg] = useState<string | null>(null);

  const [showAddOrg, setShowAddOrg] = useState(false);
  const [editOrg, setEditOrg] = useState<NhsOrg | null>(null);
  const [orgForm, setOrgForm] = useState(EMPTY_ORG);
  const [orgSaving, setOrgSaving] = useState(false);

  const [showAddSite, setShowAddSite] = useState<string | null>(null);
  const [editSite, setEditSite] = useState<NhsSite | null>(null);
  const [siteForm, setSiteForm] = useState(EMPTY_SITE);
  const [siteSaving, setSiteSaving] = useState(false);

  const { data: orgs = [], isLoading } = useQuery<NhsOrg[]>({
    queryKey: ["admin-nhs-orgs", search],
    queryFn: () => apiFetch(`/api/admin/nhs-orgs${search ? `?q=${encodeURIComponent(search)}` : ""}`),
  });

  const { data: sites = [] } = useQuery<NhsSite[]>({
    queryKey: ["admin-nhs-sites", expandedOrg],
    queryFn: () => apiFetch(`/api/admin/nhs-orgs/${expandedOrg}/sites`),
    enabled: !!expandedOrg,
  });

  interface SyncStatus {
    state: "idle" | "running" | "done" | "error";
    totalTrusts?: number;
    processedTrusts?: number;
    orgsUpserted?: number;
    sitesUpserted?: number;
    error?: string;
    startedAt?: string;
    finishedAt?: string;
  }

  const { data: syncStatus } = useQuery<SyncStatus>({
    queryKey: ["admin-nhs-sync-status"],
    queryFn: () => apiFetch("/api/admin/nhs-orgs/sync/status"),
    refetchInterval: (q) =>
      (q.state.data as SyncStatus | undefined)?.state === "running" ? 2000 : false,
  });

  const startSync = useMutation({
    mutationFn: () => apiFetch("/api/admin/nhs-orgs/sync", { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-nhs-sync-status"] });
      toast({
        title: "Sync started",
        description: "Fetching NHS Trusts and sites from the official ODS API — this takes 1–2 minutes.",
      });
    },
    onError: (e: Error) =>
      toast({ title: "Could not start sync", description: e.message, variant: "destructive" }),
  });

  const isSyncing = syncStatus?.state === "running";
  const lastSyncFinishedAt = syncStatus?.state === "done" ? syncStatus.finishedAt : undefined;
  useEffect(() => {
    if (lastSyncFinishedAt) {
      qc.invalidateQueries({ queryKey: ["admin-nhs-orgs"] });
    }
  }, [lastSyncFinishedAt, qc]);

  const deactivateOrg = useMutation({
    mutationFn: (id: number) => apiFetch(`/api/admin/nhs-orgs/${id}`, { method: "DELETE" }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-nhs-orgs"] }); toast({ title: "Organisation deactivated" }); },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const deactivateSite = useMutation({
    mutationFn: (id: number) => apiFetch(`/api/admin/nhs-orgs/sites/${id}`, { method: "DELETE" }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-nhs-sites"] }); toast({ title: "Site deactivated" }); },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const saveOrg = async () => {
    setOrgSaving(true);
    try {
      if (editOrg) {
        await apiFetch(`/api/admin/nhs-orgs/${editOrg.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(orgForm),
        });
        toast({ title: "Organisation updated" });
      } else {
        await apiFetch("/api/admin/nhs-orgs", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(orgForm),
        });
        toast({ title: "Organisation added" });
      }
      qc.invalidateQueries({ queryKey: ["admin-nhs-orgs"] });
      setShowAddOrg(false);
      setEditOrg(null);
      setOrgForm(EMPTY_ORG);
    } catch (e: unknown) {
      toast({ title: "Error", description: (e as Error).message, variant: "destructive" });
    } finally {
      setOrgSaving(false);
    }
  };

  const saveSite = async () => {
    setSiteSaving(true);
    try {
      if (editSite) {
        await apiFetch(`/api/admin/nhs-orgs/sites/${editSite.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(siteForm),
        });
        toast({ title: "Site updated" });
      } else if (showAddSite) {
        await apiFetch(`/api/admin/nhs-orgs/${showAddSite}/sites`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(siteForm),
        });
        toast({ title: "Site added" });
      }
      qc.invalidateQueries({ queryKey: ["admin-nhs-sites"] });
      setShowAddSite(null);
      setEditSite(null);
      setSiteForm(EMPTY_SITE);
    } catch (e: unknown) {
      toast({ title: "Error", description: (e as Error).message, variant: "destructive" });
    } finally {
      setSiteSaving(false);
    }
  };

  const filteredOrgs = search
    ? orgs
    : orgs;

  return (
    <AdminLayout>
      <AdminPageHeader
        title="NHS Organisations"
        description="Manage the trust and site list used during professional onboarding. Seeded from NHS ODS data; admin-added entries are flagged."
        action={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              className="gap-1.5"
              disabled={isSyncing || startSync.isPending}
              onClick={() => startSync.mutate()}
              title="Fetch the latest NHS Trust and site list from the official ODS API"
            >
              {isSyncing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <CloudDownload className="h-4 w-4" />
              )}
              {isSyncing ? "Syncing…" : "Sync from NHS ODS"}
            </Button>
            <Button
              className="bg-teal-700 hover:bg-teal-800 text-white gap-1.5"
              onClick={() => { setOrgForm(EMPTY_ORG); setEditOrg(null); setShowAddOrg(true); }}
            >
              <Plus className="h-4 w-4" /> Add organisation
            </Button>
          </div>
        }
      />

      {isSyncing && (
        <div className="mb-4 rounded-xl border border-teal-200 bg-teal-50/60 px-4 py-3 text-sm text-teal-900 flex items-center gap-3">
          <Loader2 className="h-4 w-4 animate-spin shrink-0" />
          <div className="flex-1">
            <p className="font-medium">Syncing from NHS ODS…</p>
            <p className="text-xs text-teal-800/80">
              {syncStatus?.processedTrusts ?? 0}
              {syncStatus?.totalTrusts ? ` / ${syncStatus.totalTrusts}` : ""} trusts processed
              · {syncStatus?.sitesUpserted ?? 0} sites so far. You can leave this page open — it takes 1–2 minutes.
            </p>
          </div>
        </div>
      )}

      {syncStatus?.state === "done" && (
        <div className="mb-4 rounded-xl border border-green-200 bg-green-50/60 px-4 py-3 text-sm text-green-900 flex items-center gap-3">
          <BadgeCheck className="h-4 w-4 shrink-0" />
          <p>
            Last sync complete — {syncStatus.orgsUpserted ?? 0} organisations and {syncStatus.sitesUpserted ?? 0} sites upserted.
          </p>
        </div>
      )}

      {syncStatus?.state === "error" && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50/60 px-4 py-3 text-sm text-red-900">
          <p className="font-medium">Sync failed</p>
          <p className="text-xs text-red-800/80 mt-0.5">{syncStatus.error}</p>
        </div>
      )}

      <div className="mb-4">
        <Input
          placeholder="Search organisations…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-sm"
        />
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-slate-500 py-8">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading organisations…
        </div>
      ) : filteredOrgs.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-slate-400">
          {search ? "No organisations match your search." : (
            <div className="space-y-3">
              <Network className="h-10 w-10 mx-auto text-slate-300" />
              <p className="font-medium text-slate-600">No organisations seeded yet</p>
              <p className="text-sm max-w-md mx-auto">
                Click <strong>Sync from NHS ODS</strong> above to pull the latest list of NHS Trusts and their sites from the official ODS API.
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {filteredOrgs.map((org) => {
            const expanded = expandedOrg === org.odsCode;
            const orgSites = expanded ? sites : [];
            return (
              <div key={org.id} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="flex items-center gap-3 px-4 py-3">
                  <button
                    type="button"
                    aria-label={expanded ? "Collapse sites" : "Expand sites"}
                    onClick={() => setExpandedOrg(expanded ? null : org.odsCode)}
                    className="text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </button>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-slate-900 text-sm">{org.name}</span>
                      <code className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{org.odsCode}</code>
                      {org.adminAdded && (
                        <Badge variant="outline" className="text-xs gap-1 border-teal-300 text-teal-700">
                          <BadgeCheck className="h-3 w-3" /> Admin added
                        </Badge>
                      )}
                      {org.status !== "active" && (
                        <Badge variant="secondary" className="text-xs">Inactive</Badge>
                      )}
                    </div>
                    {(org.orgType || org.region) && (
                      <p className="text-xs text-slate-400 mt-0.5">{[org.orgType, org.region].filter(Boolean).join(" · ")}</p>
                    )}
                  </div>

                  <div className="flex gap-1.5 shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-slate-400 hover:text-slate-700"
                      aria-label="Edit organisation"
                      onClick={() => {
                        setEditOrg(org);
                        setOrgForm({ odsCode: org.odsCode, name: org.name, shortName: org.shortName ?? "", orgType: org.orgType ?? "", region: org.region ?? "" });
                        setShowAddOrg(true);
                      }}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-slate-400 hover:text-red-600"
                      aria-label="Deactivate organisation"
                      onClick={() => {
                        if (confirm(`Deactivate "${org.name}"? This will also hide all its sites from the picker.`)) {
                          deactivateOrg.mutate(org.id);
                        }
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>

                {expanded && (
                  <div className="border-t border-slate-100 bg-slate-50/50 px-4 py-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Sites / Locations</p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs gap-1"
                        onClick={() => { setSiteForm(EMPTY_SITE); setEditSite(null); setShowAddSite(org.odsCode); }}
                      >
                        <Plus className="h-3 w-3" /> Add site
                      </Button>
                    </div>

                    {orgSites.length === 0 ? (
                      <p className="text-xs text-slate-400 py-2">No sites listed. Add one manually above.</p>
                    ) : (
                      <div className="space-y-1.5">
                        {orgSites.map((site) => (
                          <div key={site.id} className="flex items-center gap-2 bg-white rounded-lg border border-slate-200 px-3 py-2">
                            <MapPin className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm text-slate-800">{site.name}</span>
                                <code className="text-xs bg-slate-100 text-slate-600 px-1 py-0.5 rounded">{site.odsCode}</code>
                                {site.adminAdded && (
                                  <Badge variant="outline" className="text-xs gap-1 border-teal-300 text-teal-700 py-0">
                                    <BadgeCheck className="h-2.5 w-2.5" /> Admin
                                  </Badge>
                                )}
                                {site.status !== "active" && (
                                  <Badge variant="secondary" className="text-xs py-0">Inactive</Badge>
                                )}
                              </div>
                              {(site.city || site.postcode) && (
                                <p className="text-xs text-slate-400">{[site.city, site.postcode].filter(Boolean).join(", ")}</p>
                              )}
                            </div>
                            <div className="flex gap-1 shrink-0">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 text-slate-400 hover:text-slate-700"
                                aria-label="Edit site"
                                onClick={() => {
                                  setEditSite(site);
                                  setSiteForm({ odsCode: site.odsCode, name: site.name, address: site.address ?? "", city: site.city ?? "", postcode: site.postcode ?? "" });
                                  setShowAddSite(site.orgOdsCode);
                                }}
                              >
                                <Pencil className="h-3 w-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 text-slate-400 hover:text-red-600"
                                aria-label="Deactivate site"
                                onClick={() => {
                                  if (confirm(`Deactivate site "${site.name}"?`)) deactivateSite.mutate(site.id);
                                }}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={showAddOrg} onOpenChange={(o) => { if (!o) { setShowAddOrg(false); setEditOrg(null); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editOrg ? "Edit organisation" : "Add organisation"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>ODS code *</Label>
              <Input
                placeholder="e.g. RJF"
                value={orgForm.odsCode}
                onChange={(e) => setOrgForm({ ...orgForm, odsCode: e.target.value.toUpperCase() })}
                disabled={!!editOrg}
              />
            </div>
            <div>
              <Label>Full name *</Label>
              <Input
                placeholder="e.g. University Hospitals of Derby and Burton NHS Foundation Trust"
                value={orgForm.name}
                onChange={(e) => setOrgForm({ ...orgForm, name: e.target.value })}
              />
            </div>
            <div>
              <Label>Short name <span className="text-slate-400 font-normal">(optional)</span></Label>
              <Input
                placeholder="e.g. UHDB"
                value={orgForm.shortName}
                onChange={(e) => setOrgForm({ ...orgForm, shortName: e.target.value })}
              />
            </div>
            <div>
              <Label>Organisation type <span className="text-slate-400 font-normal">(optional)</span></Label>
              <Input
                placeholder="e.g. NHS Foundation Trust"
                value={orgForm.orgType}
                onChange={(e) => setOrgForm({ ...orgForm, orgType: e.target.value })}
              />
            </div>
            <div>
              <Label>Region <span className="text-slate-400 font-normal">(optional)</span></Label>
              <Input
                placeholder="e.g. Midlands"
                value={orgForm.region}
                onChange={(e) => setOrgForm({ ...orgForm, region: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAddOrg(false); setEditOrg(null); }}>Cancel</Button>
            <Button
              className="bg-teal-700 hover:bg-teal-800 text-white"
              disabled={orgSaving || !orgForm.odsCode.trim() || !orgForm.name.trim()}
              onClick={saveOrg}
            >
              {orgSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editOrg ? "Save changes" : "Add organisation")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!showAddSite} onOpenChange={(o) => { if (!o) { setShowAddSite(null); setEditSite(null); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editSite ? "Edit site" : "Add site"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>ODS site code *</Label>
              <Input
                placeholder="e.g. RJF01"
                value={siteForm.odsCode}
                onChange={(e) => setSiteForm({ ...siteForm, odsCode: e.target.value.toUpperCase() })}
                disabled={!!editSite}
              />
            </div>
            <div>
              <Label>Site name *</Label>
              <Input
                placeholder="e.g. Royal Derby Hospital"
                value={siteForm.name}
                onChange={(e) => setSiteForm({ ...siteForm, name: e.target.value })}
              />
            </div>
            <div>
              <Label>Address <span className="text-slate-400 font-normal">(optional)</span></Label>
              <Input
                placeholder="e.g. Uttoxeter Road"
                value={siteForm.address}
                onChange={(e) => setSiteForm({ ...siteForm, address: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>City <span className="text-slate-400 font-normal">(optional)</span></Label>
                <Input
                  placeholder="e.g. Derby"
                  value={siteForm.city}
                  onChange={(e) => setSiteForm({ ...siteForm, city: e.target.value })}
                />
              </div>
              <div>
                <Label>Postcode <span className="text-slate-400 font-normal">(optional)</span></Label>
                <Input
                  placeholder="e.g. DE22 3NE"
                  value={siteForm.postcode}
                  onChange={(e) => setSiteForm({ ...siteForm, postcode: e.target.value })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAddSite(null); setEditSite(null); }}>Cancel</Button>
            <Button
              className="bg-teal-700 hover:bg-teal-800 text-white"
              disabled={siteSaving || !siteForm.odsCode.trim() || !siteForm.name.trim()}
              onClick={saveSite}
            >
              {siteSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editSite ? "Save changes" : "Add site")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

export { RefreshCw };
